'use client';

import React, { useEffect, useState } from 'react';
import Header from './header'; // Assuming Header.js is in the same directory
import onstationLogo from './Assets/onstationBlack.png'; // Adjust path as needed
import haulhub from './Assets/eDotLogo.svg'; // Adjust path as needed
import whiteRoad from './Assets/whiteRoad.png'; // Adjust path as needed
import ames from './Assets/ames-logo.svg'
import onstation1 from './Assets/onstation1.png'; // Adjust path as needed
import onstation2 from'./Assets/onstation2.png'; // Adjust path as needed
import onstation3 from './Assets/onstation3.png'; // Adjust path as needed
import Footer from './footer';


const OnStationModal = ({ isOpen, onClose, selectedStep, setSelectedStep }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-0 sm:p-4 overflow-hidden">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity" 
          onClick={onClose}
          aria-hidden="true"
        ></div>
        
        {/* Modal container with height and scroll constraints */}
        <div className="fixed inset-0 z-10 overflow-y-auto pt-[60px] sm:pt-0 pb-0 px-5 md:mt-10">
          <div className="min-h-full flex items-center justify-center p-0 sm:p-4">
            {/* Modal dialog */}
            <div className="relative bg-white w-full sm:max-w-3xl mx-auto rounded-t-xl sm:rounded-xl overflow-hidden shadow-2xl transform transition-all max-h-[calc(100vh-60px)] sm:max-h-[90vh] flex flex-col">
              {/* Close button */}
              <button 
                onClick={onClose} 
                className="absolute top-3 right-3 z-20 bg-gray-200 p-1.5 sm:p-2 rounded-full text-gray-800 hover:text-yellow-500 transition-colors"
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Header section - more compact for mobile */}
              <div className="p-3 sm:p-5 bg-yellow-50 shrink-0">
                <div className="flex items-center mb-2">
                  <img src={onstationLogo} alt="OnStation Logo" className="h-7 sm:h-8 w-auto mr-3" />
                </div>
                <p className="text-gray-700 text-xs sm:text-sm mb-3">
                  Seamlessly transmit non-nuclear density readings from TransTech Connect to the OnStation Portal. 
                  Eliminate manual entry, reduce errors, and access live, geotagged data for real-time project adjustments.
                </p>
                
                {/* Tab navigation - more compact for mobile */}
                <div className="flex justify-center">
                  <nav className="w-full sm:w-auto inline-flex rounded-lg shadow-md bg-white p-1" aria-label="Integration Steps">
                    {["Configure", "Select Data", "Send"].map((step, index) => (
                      <button
                        key={step}
                        onClick={() => setSelectedStep(index + 1)}
                        className={`flex-1 sm:flex-initial px-2 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                          selectedStep === index + 1
                            ? "bg-yellow-500 text-white"
                            : "text-gray-700 hover:text-gray-900"
                        }`}
                        aria-current={selectedStep === index + 1 ? "page" : undefined}
                      >
                        {step}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
              
              {/* Content section - with automatic overflow */}
              <div className="p-3 sm:p-5 bg-white overflow-y-auto flex-grow">
                <div className="transition-all duration-300">
                  {selectedStep === 1 && (
                    <div className="animate-fade-in">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 text-center">Configure Your Integration</h3>
                      <p className="text-gray-700 text-xs sm:text-sm mb-3 text-center">
                        Connect TransTech Connect to your OnStation account with a simple API key. Set up once and transfer data forever.
                      </p>
                      <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden shadow-md">
                        <img 
                          src={onstation1} 
                          alt="OnStation Configuration" 
                          className="w-full h-full object-contain" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/api/placeholder/800/450";
                            e.target.alt = "OnStation Configuration (placeholder)";
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {selectedStep === 2 && (
                    <div className="animate-fade-in">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 text-center">Select Your Data</h3>
                      <p className="text-gray-700 text-xs sm:text-sm mb-3 text-center">
                        Choose which measurements to send to OnStation, filtering by date, location, or specific readings.
                      </p>
                      <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden shadow-md">
                        <img 
                          src={onstation2} 
                          alt="Data Selection Interface" 
                          className="w-full h-full object-contain" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/api/placeholder/800/450";
                            e.target.alt = "Data Selection Interface (placeholder)";
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {selectedStep === 3 && (
                    <div className="animate-fade-in">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 text-center">Send to OnStation</h3>
                      <p className="text-gray-700 text-xs sm:text-sm mb-3 text-center">
                        See your measurements instantly appear on your OnStation project map with color-coded indicators.
                      </p>
                      <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden shadow-md">
                        <img 
                          src={onstation3} 
                          alt="OnStation Map Visualization" 
                          className="w-full h-full object-contain" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/api/placeholder/800/450";
                            e.target.alt = "OnStation Map Visualization (placeholder)";
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Footer section - more compact for mobile */}
              <div className="p-3 sm:p-4 bg-gray-100 border-t border-gray-200 shrink-0">
                <div className="flex justify-between items-center gap-2">
                  <a
                    href="https://finance.yahoo.com/news/transforming-construction-data-onstation-integrates-141100441.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-500 text-xs sm:text-sm font-medium hover:text-yellow-600 transition-colors whitespace-nowrap"
                  >
                    Press Release
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 ml-1 inline" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </a>
                  
                  <a
                    href="https://www.onstationapp.com/integrations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-yellow-500 text-white font-medium py-1.5 sm:py-2 px-3 sm:px-5 rounded-lg hover:bg-yellow-600 transition-colors text-xs sm:text-sm"
                  >
                    Learn More
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  export default function Integrations() {
    const [isOnStationModalOpen, setIsOnStationModalOpen] = useState(false);
    const [selectedStep, setSelectedStep] = useState(1);
    
    useEffect(() => {
      document.title = 'TransTech Connect | Integrations';
      return () => {
        document.title = 'TransTech Connect';
      };
    }, []);
  
    // Function to handle opening the OnStation modal
    const openOnStationModal = (e) => {
      e.preventDefault();
      setIsOnStationModalOpen(true);
    };
  
    return (
      <div className="bg-gray-100 text-gray-900 min-h-screen font-sans">
        <Header />
  
        <main className="isolate">
          {/* Hero Section */}
          <div className="relative pt-14">
            <div className="py-12 sm:py-20 relative">
              <img
                src={whiteRoad}
                alt="Background"
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
              <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-2xl text-center">
                  <h1 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 sm:text-6xl animate-fade-in">
                    Our Integration Partners
                  </h1>
                  <p className="mt-8 text-pretty text-lg font-medium text-gray-600 sm:text-xl/8 animate-slide-up">
                    Connect TransTech Connect with leading platforms to streamline your workflow.
                  </p>
                  <div className="mt-10 flex items-center justify-center gap-x-6">
                    <a
                      href="https://transtechsys.com/products/requestIntegration"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-yellow-500 text-gray-900 font-semibold py-3 px-8 rounded-lg hover:bg-yellow-600 transition duration-300 transform hover:scale-105 animate-pulse-slow"
                    >
                      Request an Integration
                    </a>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          {/* Integrations List Section */}
          <section className="py-20 bg-white">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
                {/* OnStation Integration - Modified to open modal */}
                <div
                  className="relative -m-2 rounded-xl bg-gray-100 p-6 ring-1 ring-inset ring-gray-200 lg:-m-4 lg:rounded-2xl lg:p-8 shadow-md hover:shadow-lg hover:shadow-yellow-500/20 transition-all duration-300 animate-fade-in"
                >
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={onstationLogo}
                      alt="OnStation Logo"
                      className="h-12 w-auto mb-4 object-fit"
                    />
                    <h3 className="text-xl font-semibold text-black mb-2">OnStation</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Seamlessly transmit non-nuclear density readings from the PQI 380+ gauge to the OnStation Portal. Eliminate manual entry, reduce errors, and access live, geotagged data for real-time project adjustments.
                    </p>
                    <div className="flex gap-4">
                      <a
                        onClick={openOnStationModal}
                        className="text-yellow-500 font-semibold hover:text-yellow-600 transition duration-300"
                      >
                        View Details <span aria-hidden="true">→</span>
                      </a>
                    </div>
                  </div>
                </div>
  
                {/* Other integrations */}
                <div
                  className="relative -m-2 rounded-xl bg-gray-100 p-6 ring-1 ring-inset ring-gray-200 lg:-m-4 lg:rounded-2xl lg:p-8 shadow-md hover:shadow-lg hover:shadow-yellow-500/20 transition-all duration-300 animate-fade-in"
                >
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={ames}
                      alt="Ames Engineering Logo"
                      className="h-12 w-auto mb-4 object-fit"
                    />
                    <h3 className="text-xl font-semibold text-black mb-2">Ames Engineering</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Bringing Ames industry leading road profiling data into TransTech Connect. Capturing another piece of the value chain for management and distribution. Visualize data, generate reports, and avoid data silos.
                    </p>
                    <a
                      href="https://www.amesengineering.com/products/8300-high-speed-profiler"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-yellow-500 font-semibold hover:text-yellow-600 transition duration-300"
                    >
                      Learn More <span aria-hidden="true">→</span>
                    </a>
                  </div>
                </div>
  
                {/* <div
                  className="relative -m-2 rounded-xl bg-gray-100 p-6 ring-1 ring-inset ring-gray-200 lg:-m-4 lg:rounded-2xl lg:p-8 shadow-md hover:shadow-lg hover:shadow-yellow-500/20 transition-all duration-300 animate-fade-in"
                >
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={haulhub}
                      alt="EDOT by HaulHub Logo"
                      className="h-12 w-auto mb-4 object-fit"
                    />
                    <h3 className="text-xl font-semibold text-black mb-2">EDOT by HaulHub</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      HaulHub leads the way in technology innovation for state transportation agencies, leveraging automation to promote safety, transparency, and superior construction management outcomes.
                    </p>
                    <span className="text-yellow-500 font-semibold">
                      Coming Soon...
                    </span>
                  </div>
                </div> */}
              </div>
            </div>
          </section>
  
         
          
          {/* OnStation Modal */}
          <OnStationModal 
            isOpen={isOnStationModalOpen} 
            onClose={() => setIsOnStationModalOpen(false)} 
            selectedStep={selectedStep}
            setSelectedStep={setSelectedStep}
          />
        </main>
  
        <Footer />

      </div>
    );
  }